import { BOARD, MAX_LEVEL, groupTiles } from './board'
import { QUARANTINE_BAIL, canUpgrade, dealDown, downPayment, upgradeCostFor } from './engine'
import { hasEffect, totalExpenses } from './finance'
import type { DealCard, GameState, Player } from './types'

/** เงินสดสำรองที่บอทอยากเหลือไว้ — ยิ่งรายจ่ายสูงยิ่งต้องกันมาก */
function reserve(p: Player): number {
  return Math.round(totalExpenses(p) * 1.4)
}

export type BuyDecision = 'cash' | 'loan' | 'skip'

/** บอทตัดสินใจซื้อโฉนด: ให้น้ำหนักกับการปิดกลุ่มสี และรักษาเงินสดสำรอง */
export function botBuyProperty(g: GameState, tileId: number): BuyDecision {
  const p = g.players[g.current]
  const tile = BOARD[tileId]
  const price = tile.price ?? 0
  const res = reserve(p)

  let appeal = 1
  if (tile.kind === 'property') {
    const group = groupTiles(tile.group!)
    const mine = group.filter((t) => g.tiles[t.id].ownerId === p.id).length
    const enemy = group.filter(
      (t) => g.tiles[t.id].ownerId !== null && g.tiles[t.id].ownerId !== p.id,
    ).length
    if (mine === group.length - 1) appeal = 2.2 // ปิดกลุ่มได้เลย
    else if (mine > 0) appeal = 1.5
    if (enemy === group.length - 1) appeal = 1.4 // บล็อกคู่แข่งไม่ให้ปิดกลุ่ม
  } else if (tile.kind === 'transport') {
    const owned = BOARD.filter((t) => t.kind === 'transport' && g.tiles[t.id].ownerId === p.id).length
    appeal = 1.3 + owned * 0.35
  } else if (tile.kind === 'utility') {
    appeal = 1.15
  }

  // ซื้อสดถ้าเงินยังเหลือสบาย
  if (p.cash - price >= res) return 'cash'

  const down = downPayment(p, price)
  const loan = price - down
  const monthlyIncome = tile.monthly?.[0] ?? 0
  const monthlyCost = Math.round((loan * 0.01) / 100) * 100
  const netMonthly = monthlyIncome - monthlyCost

  // ผ่อนได้ถ้าเงินสดยังเหลือสำรอง และกระแสเงินสดไม่ติดลบเกินไป
  if (p.cash - down >= res * 0.6 && netMonthly > -monthlyIncome * 0.35 && appeal >= 1.2) return 'loan'
  if (p.cash - down >= res && netMonthly >= 0) return 'loan'
  return 'skip'
}

export function botChooseDeal(cards: DealCard[]): DealCard {
  return cards.reduce((best, c) => {
    const roi = c.cashflow / Math.max(1, dealDown(c))
    const bestRoi = best.cashflow / Math.max(1, dealDown(best))
    return roi > bestRoi ? c : best
  })
}

/** ยอมรับดีลถ้าผลตอบแทนต่อเงินดาวน์คุ้ม และยังมีเงินสำรองเหลือ */
export function botAcceptDeal(g: GameState, card: DealCard): boolean {
  const p = g.players[g.current]
  const down = dealDown(card)
  // บัญชีถูกแฮกอยู่ก็ปิดดีลไม่ได้ ต้องยอมปล่อยไปไม่งั้นเทิร์นจะค้าง
  if (hasEffect(g, p, 'noBuy')) return false
  if (p.cash < down) return false
  const roi = card.cashflow / down // ต่อเดือน
  const res = reserve(p)
  if (p.cash - down < res * 0.5) return false
  if (card.cashflow === 0) return p.cash - down > res * 3 // เก็งกำไรเฉย ๆ ต้องรวยจริง
  return roi >= 0.011 || (roi >= 0.008 && p.cash - down > res * 2)
}

/** ช่องที่บอทอยากอัปเกรด — เรียงจากกลุ่มที่คุ้มที่สุด */
export function botUpgradeTarget(g: GameState): number | null {
  const p = g.players[g.current]
  const res = reserve(p)
  const candidates = g.tiles
    .map((_, id) => id)
    .filter((id) => canUpgrade(g, p, id))
    .filter((id) => p.cash - upgradeCostFor(p, BOARD[id]) >= res * 1.5)
    .sort((a, b) => {
      const la = g.tiles[a].level
      const lb = g.tiles[b].level
      if (la !== lb) return la - lb
      return (BOARD[b].monthly?.[MAX_LEVEL] ?? 0) - (BOARD[a].monthly?.[MAX_LEVEL] ?? 0)
    })
  return candidates[0] ?? null
}

export function botPayBail(g: GameState): boolean {
  const p = g.players[g.current]
  return p.cash - QUARANTINE_BAIL >= reserve(p) * 2
}

export type RaiseAction =
  | { t: 'sellAsset'; uid: string }
  | { t: 'mortgage'; tileId: number }
  | { t: 'sellProperty'; tileId: number }
  | null

/**
 * ลำดับการระดมเงินของบอทเมื่อเงินสดติดลบ:
 * 1) ขายสินทรัพย์ที่ให้กระแสเงินสดต่ำสุดต่อบาท
 * 2) จำนำโฉนดที่ยังไม่ได้อัปเกรด
 * 3) ขายโฉนดทิ้ง (ทางสุดท้าย)
 */
export function botRaiseCash(g: GameState): RaiseAction {
  const p = g.players[g.current]

  const worstAsset = p.assets
    .slice()
    .sort((a, b) => a.cashflow / Math.max(1, a.value) - b.cashflow / Math.max(1, b.value))[0]
  if (worstAsset) return { t: 'sellAsset', uid: worstAsset.uid }

  const mortgageable = g.tiles
    .map((ts, id) => ({ ts, id }))
    .filter((x) => x.ts.ownerId === p.id && !x.ts.mortgaged)
    .sort((a, b) => a.ts.level - b.ts.level || (BOARD[a.id].price ?? 0) - (BOARD[b.id].price ?? 0))[0]
  if (mortgageable) return { t: 'mortgage', tileId: mortgageable.id }

  const sellable = g.tiles
    .map((ts, id) => ({ ts, id }))
    .filter((x) => x.ts.ownerId === p.id)
    .sort((a, b) => (BOARD[a.id].price ?? 0) - (BOARD[b.id].price ?? 0))[0]
  if (sellable) return { t: 'sellProperty', tileId: sellable.id }

  return null
}
